import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateUser } from '@/services/noteGoApi/noteGoApiComponents';
import useAlertStore from '@/stores/alertStore';
import { config } from '@/config';
import { useTagCounts } from '@/hooks/useTagCounts';

interface UseManageTagsReturn {
  isLoading: boolean;
  tags: string[];
  tagCounts: Record<string, number>;
  openDeleteDialog: boolean;
  openEditDialog: boolean;
  openMergeDialog: boolean;
  selectedTag: string;
  newTagName: string;
  tagsToMerge: string[];
  setNewTagName: (value: string) => void;
  setTagsToMerge: (value: string[]) => void;
  handleOpenDeleteDialog: (tag: string) => void;
  handleCloseDeleteDialog: () => void;
  handleDeleteTag: () => Promise<void>;
  handleOpenEditDialog: (tag: string) => void;
  handleCloseEditDialog: () => void;
  handleEditTag: () => Promise<void>;
  handleOpenMergeDialog: (tag?: string) => void;
  handleCloseMergeDialog: () => void;
  handleMergeTags: () => Promise<void>;
}

export const useManageTags = (): UseManageTagsReturn => {
  const { userData, userToken, fetchAndSetUserData } = useAuthStore();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { showAlert } = useAlertStore();
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);

  // Manage Tags operates on the user's OWN notes only — shared notes' tags can't be edited here (CONTRACT #3)
  const { tagCounts, isLoading: isLoadingCounts, refetch: refetchTagCounts } = useTagCounts({ shared: false });

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openMergeDialog, setOpenMergeDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [tagsToMerge, setTagsToMerge] = useState<string[]>([]);

  useEffect(() => {
    if (!isLoadingCounts) {
      const tagNames = Object.keys(tagCounts).sort((a, b) => a.localeCompare(b));
      setTags(tagNames);
    }
  }, [tagCounts, isLoadingCounts]);

  // Merge the operation into the user's existing tag list: drop only the removed
  // tags and add the renamed/merged tag. Unrelated tags (including tags currently
  // on zero notes) must survive — never replace the list with Object.keys(tagCounts).
  const handleUpdateTags = async (removedTags: string[], addedTag?: string) => {
    if (!userData) return;

    const removed = new Set(removedTags.map((tag) => tag.trim().toLowerCase()));
    const updatedTags = (userData.tags ?? []).filter((tag) => !removed.has(tag.trim().toLowerCase()));
    if (addedTag && !updatedTags.some((tag) => tag.trim().toLowerCase() === addedTag.trim().toLowerCase())) {
      updatedTags.push(addedTag);
    }

    setIsLoading(true);
    try {
      await updateUser({
        pathParams: { userId: userData.id },
        body: {
          tags: updatedTags,
        },
        headers: { authorization: `Bearer ${userToken}` },
      });
      fetchAndSetUserData({ force: true });
      refetchTagCounts();
      setIsLoading(false);
    } catch (error) {
      showAlert('Failed to update tags', 'error');
      setIsLoading(false);
      throw error;
    }
  };

  const handleOpenDeleteDialog = (tag: string) => {
    setSelectedTag(tag);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteTag = async () => {
    try {
      setIsLoading(true);

      const response = await fetch(`${config.apiUrl}/tags/merge`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${userToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          source_tag_ids: [selectedTag],
          target_tag_name: '',
          target_tag_color: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Delete failed');
      }

      const result = await response.json();

      await handleUpdateTags([selectedTag]);

      showAlert(`Tag deleted successfully. ${result.updated_note_count} notes updated.`, 'success');
      handleCloseDeleteDialog();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error deleting tag. Please try again.';
      showAlert(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  const handleOpenEditDialog = (tag: string) => {
    setSelectedTag(tag);
    setNewTagName(tag);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setNewTagName('');
  };

  const handleEditTag = async () => {
    if (!newTagName.trim()) {
      showAlert('Tag name cannot be empty', 'error');
      return;
    }

    if (tags.includes(newTagName) && newTagName !== selectedTag) {
      showAlert('This tag already exists', 'error');
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${config.apiUrl}/tags/merge`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${userToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          source_tag_ids: [selectedTag],
          target_tag_name: newTagName,
          target_tag_color: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Update failed');
      }

      const result = await response.json();

      await handleUpdateTags([selectedTag], newTagName);

      showAlert(`Tag updated successfully. ${result.updated_note_count} notes updated.`, 'success');
      handleCloseEditDialog();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error updating tag. Please try again.';
      showAlert(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  const handleOpenMergeDialog = (tag: string = '') => {
    setSelectedTag(tag);
    setNewTagName('');
    setTagsToMerge(tag ? [tag] : []);
    setOpenMergeDialog(true);
  };

  const handleCloseMergeDialog = () => {
    setOpenMergeDialog(false);
    setNewTagName('');
    setTagsToMerge([]);
  };

  const handleMergeTags = async () => {
    if (tagsToMerge.length < 2) {
      showAlert('Please select at least 2 tags to merge', 'error');
      return;
    }

    if (!newTagName.trim()) {
      showAlert('Merged tag name cannot be empty', 'error');
      return;
    }

    if (tags.includes(newTagName) && !tagsToMerge.includes(newTagName)) {
      showAlert('This tag already exists', 'error');
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${config.apiUrl}/tags/merge`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${userToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          source_tag_ids: tagsToMerge,
          target_tag_name: newTagName,
          target_tag_color: null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Merge failed');
      }

      const result = await response.json();

      await handleUpdateTags(tagsToMerge, newTagName);

      showAlert(`Tags merged successfully. ${result.updated_note_count} notes updated.`, 'success');
      handleCloseMergeDialog();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error merging tags. Please try again.';
      showAlert(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    tags,
    tagCounts,
    openDeleteDialog,
    openEditDialog,
    openMergeDialog,
    selectedTag,
    newTagName,
    tagsToMerge,
    setNewTagName,
    setTagsToMerge,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDeleteTag,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleEditTag,
    handleOpenMergeDialog,
    handleCloseMergeDialog,
    handleMergeTags,
  };
};
